/*
 * How long a text message is, in the units a carrier bills.
 *
 * A church needs to be told what a send will cost and why, and "why" is almost always the same story: one character
 * that is not in the GSM alphabet moved the whole message from 160 characters per part to 70. An em dash pasted from
 * a word processor, a curly quote, an emoji, an accented name — each turns a one-part message into two or three.
 *
 * Nothing here depends on a provider. A segment is a carrier fact: GSM 03.38 packs seven bits per character into
 * 140 octets, giving 160 per single message and 153 per part once a concatenation header takes six of them. Anything
 * outside that alphabet is sent as UCS-2, sixteen bits per character: 70 per message, 67 per part.
 */

/** The GSM 03.38 basic alphabet. Each of these costs one septet. */
const GSM_BASIC = new Set(
  [
    "@£$¥èéùìòÇ\nØø\rÅå",
    "Δ_ΦΓΛΩΠΨΣΘΞ",
    "ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?",
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§",
    "¿abcdefghijklmnopqrstuvwxyzäöñüà",
  ]
    .join("")
    .split(""),
);

/**
 * The GSM extension table. Each of these is sent as an escape plus the character, so it costs **two** septets — the
 * usual reason a message a church thought was 159 characters turns out to be two parts.
 */
const GSM_EXTENDED = new Set(["^", "{", "}", "\\", "[", "~", "]", "|", "€"]);

/** Form feed is in the extension table too, and is the one people never think of. */
GSM_EXTENDED.add("\f");

export type Encoding = "gsm-7" | "ucs-2";

export interface Measurement {
  readonly encoding: Encoding;
  /** What the carrier counts: septets for GSM-7, UTF-16 code units for UCS-2. */
  readonly units: number;
  readonly segments: number;
  /** Units left before another segment is needed. Zero when the message is exactly full. */
  readonly remaining: number;
  /** The characters that forced UCS-2, in the order they appear, without repeats. Empty for a GSM-7 message. */
  readonly forcedUnicodeBy: readonly string[];
}

const LIMITS = {
  "gsm-7": { single: 160, concatenated: 153 },
  "ucs-2": { single: 70, concatenated: 67 },
} as const;

/**
 * Measures a message the way a carrier will.
 *
 * Note on emoji and other characters outside the basic plane: they are two UTF-16 code units each, and a carrier
 * counts code units, not characters. So one emoji costs two of the seventy. Iterating the string by code point and
 * adding the length of each keeps that correct without special-casing surrogate pairs.
 */
export function measure(text: string): Measurement {
  const forced: string[] = [];
  let unicode = false;
  let septets = 0;
  let codeUnits = 0;

  for (const character of text) {
    codeUnits += character.length;
    if (GSM_BASIC.has(character)) {
      septets += 1;
    } else if (GSM_EXTENDED.has(character)) {
      septets += 2;
    } else {
      unicode = true;
      if (!forced.includes(character)) forced.push(character);
    }
  }

  const encoding: Encoding = unicode ? "ucs-2" : "gsm-7";
  const units = unicode ? codeUnits : septets;
  const { single, concatenated } = LIMITS[encoding];

  if (units === 0)
    return { encoding, units: 0, segments: 0, remaining: single, forcedUnicodeBy: [] };

  const segments = units <= single ? 1 : Math.ceil(units / concatenated);
  const capacity = segments === 1 ? single : segments * concatenated;

  return {
    encoding,
    units,
    segments,
    remaining: capacity - units,
    forcedUnicodeBy: forced,
  };
}

/**
 * What a church should be told, in one sentence, when a message costs more than they expect.
 *
 * Returns null when there is nothing worth saying — which is most of the time, and a warning that appears every time
 * is one nobody reads.
 */
export function explainCost(text: string): string | null {
  const { encoding, segments, forcedUnicodeBy } = measure(text);
  if (segments <= 1) return null;
  if (encoding === "ucs-2" && forcedUnicodeBy.length > 0) {
    const shown = forcedUnicodeBy.slice(0, 3).join(" ");
    return `This message is ${String(segments)} parts, and is charged as ${String(segments)}. It would be shorter without ${shown}, which cannot be sent in the plain text alphabet and cuts each part from 160 characters to 70.`;
  }
  return `This message is ${String(segments)} parts, and is charged as ${String(segments)}.`;
}
